import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { socialApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import StarButton from '../components/StarButton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Star, Eye, Filter, Bookmark } from 'lucide-react';
import { toast } from 'sonner';

const StarredPage = () => {
  const { user } = useAuth();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadStarredSkills();
  }, []);

  const loadStarredSkills = async () => {
    try {
      setLoading(true);
      const res = await socialApi.starredSkills();
      const starredSkills = res.data || [];
      setSkills(starredSkills);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(starredSkills.map(s => s.category_id).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Failed to load starred skills', error);
      toast.error('Failed to load starred skills');
    } finally {
      setLoading(false);
    }
  };

  const filteredSkills = selectedCategory === 'all' 
    ? skills 
    : skills.filter(skill => skill.category_id === selectedCategory);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
          Starred Skills
        </h1>
        <p className="text-lg text-text-gray dark:text-dark-text-secondary">
          Skills you've bookmarked for later
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-primary/20 to-primary-dark/20 rounded-xl">
              <Bookmark className="h-6 w-6 text-primary" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1">
            {skills.length}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            Total Starred
          </div>
        </GlassCard>

        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-xl">
              <Filter className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1">
            {categories.length}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            Categories
          </div>
        </GlassCard>

        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl">
              <Eye className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1">
            {filteredSkills.length}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            {selectedCategory === 'all' ? 'All Skills' : 'Filtered'}
          </div>
        </GlassCard>
      </div>

      {/* Category Filter */}
      <GlassCard variant="strong" className="p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Filter className="h-5 w-5 text-text-gray dark:text-dark-text-secondary" />
            <span className="text-sm font-semibold text-text-dark dark:text-dark-text">
              Filter by Category
            </span>
          </div>
          
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[200px] glass border-0">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="glass-strong">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Active Filter Display */}
        {selectedCategory !== 'all' && (
          <div className="mt-4 flex items-center gap-2">
            <Badge className="bg-primary/10 text-primary border-0">
              {selectedCategory}
            </Badge>
            <button
              onClick={() => setSelectedCategory('all')}
              className="text-xs text-text-gray dark:text-dark-text-secondary hover:text-primary transition-colors"
            >
              Clear filter
            </button>
          </div>
        )}
      </GlassCard>

      {/* Skills Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <GlassCard key={i} className="h-64 skeleton"></GlassCard>
          ))}
        </div>
      ) : filteredSkills.length === 0 ? (
        <GlassCard variant="strong" className="text-center py-16">
          <div className="inline-block p-6 bg-gradient-to-br from-primary/10 to-primary-dark/10 rounded-2xl mb-4">
            <Bookmark className="h-16 w-16 text-primary mx-auto" />
          </div>
          <h3 className="text-2xl font-bold text-text-dark dark:text-dark-text mb-2">
            {selectedCategory === 'all' ? 'No starred skills yet' : 'No skills in this category'}
          </h3>
          <p className="text-text-gray dark:text-dark-text-secondary mb-6 max-w-md mx-auto">
            {selectedCategory === 'all' 
              ? 'Star skills to save them for later and build your collection'
              : 'Try a different category or clear the filter'}
          </p>
          {selectedCategory !== 'all' && (
            <Button 
              onClick={() => setSelectedCategory('all')}
              variant="outline"
              className="rounded-xl"
            >
              Clear Filter
            </Button>
          )}
        </GlassCard>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSkills.map((skill) => (
            <Link key={skill.id} to={`/skills/${skill.id}`}>
              <GlassCard hover className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-manrope font-bold text-text-dark dark:text-dark-text">
                    {skill.name}
                  </h3>
                  <Star className="h-5 w-5 text-primary fill-primary flex-shrink-0 ml-2" />
                </div>
                
                <p className="text-sm text-text-gray dark:text-dark-text-secondary line-clamp-2 mb-4">
                  {skill.description || 'No description'}
                </p>
                
                {/* Category Badge */}
                {skill.category && (
                  <Badge className="bg-primary/10 text-primary border-0 mb-4">
                    {typeof skill.category === 'object' ? skill.category.name : skill.category}
                  </Badge>
                )}
                
                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-text-gray dark:text-dark-text-secondary">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4" />
                    <span>{skill.star_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    <span>{skill.view_count || 0}</span>
                  </div>
                </div>
              </GlassCard>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default StarredPage;
